// Estado da janela de 24h do WhatsApp, do lado do front.
//
// Fonte única da pergunta "essa conversa ainda pode receber texto livre?" — a mesma
// resposta governa o composer (trava em template), a badge da lista, o botão Concluir
// e o recorte de "Janela expirada". Antes a expressão estava duplicada em três lugares
// e cada cópia podia divergir.
//
// Por RELÓGIO, não pelo flag `windowState` do backend: ele só vira CLOSED no sweep de
// 15min, e nesse vão a conversa já não aceita mais envio livre. Espelha `isWindowOpen`
// do backend (`src/modules/conversations/window.ts`).
//
// MET-25: mora em `lib/utils` (e não mais em `features/inbox/utils`) porque o painel
// de indicadores lê a janela fora do inbox, e feature importar util de outra feature é
// proibido pela convenção do repo. `features/inbox` reexporta pelo barrel.

/**
 * Campos mínimos para decidir — subconjunto de ConversationSummary/Detail.
 *
 * `status` é `string` de propósito: o union `ConversationStatus` vive na API do
 * inbox, e um util de `lib/` não pode importar de uma feature. Só `'DONE'` muda a
 * decisão; qualquer outro valor passa direto.
 */
export interface WindowFields {
  windowClosesAt: string | null;
  status: string;
}

/**
 * Passaram-se as 24h desde o último inbound do cliente. Pura pergunta de envio:
 * a partir daqui só sai TEMPLATE aprovado, valendo INCLUSIVE para conversa `DONE`
 * (o composer precisa travar do mesmo jeito). Use nas decisões de envio.
 */
export function isWindowClosedByClock(
  windowClosesAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  return !!windowClosesAt && Date.parse(windowClosesAt) <= now;
}

/**
 * Janela vencida numa conversa ainda ABERTA: ninguém está atendendo (a IA não
 * responde mais e a Meta recusa texto livre), o retorno do cliente abre conversa
 * NOVA, e o que resta é um encerramento manual. É este o recorte de "Janela
 * expirada" na Fila e na lista. `DONE` já foi encerrada — não é pendência.
 */
export function isWindowExpired(conv: WindowFields, now: number = Date.now()): boolean {
  if (conv.status === 'DONE') return false;
  return isWindowClosedByClock(conv.windowClosesAt, now);
}

/**
 * Milissegundos até a janela fechar. `null` quando não há janela conhecida
 * (`windowClosesAt` ausente) — "sem dado" nunca vira `0`, que a UI leria como
 * "expira agora". Negativo significa vencida há tanto tempo.
 */
export function windowRemainingMs(
  windowClosesAt: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!windowClosesAt) return null;
  const closesAt = Date.parse(windowClosesAt);
  if (Number.isNaN(closesAt)) return null;
  return closesAt - now;
}
